'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import BgOrbs from './BgOrbs';
import { formatStatValue, type LandingStats } from '@shared/services/landingStatsFormat';

/**
 * TrustStrip — social proof между обещанием маскота (Scene 5) и фичами (Scene 6).
 *
 * Тёмный фон продолжает cinematic-настроение Scene 5, добавляет ритм перед
 * светлым Scene 6. Четыре крупных числа с count-up анимацией при scroll-in.
 *
 * Цифры приходят из landing_stats (ежечасный snapshot от refreshLandingStats
 * в TG-боте). Pure-форматтер formatStatValue даёт точное число до 9999 и
 * «k+»-вид от 10000.
 */

interface Props {
  stats: LandingStats;
}

export default function TrustStrip({ stats }: Props) {
  const reduced = useReducedMotion() ?? false;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  const items = [
    { value: stats.users,    label: 'человек\nпользуются' },
    { value: stats.entries,  label: 'сохранёнок\nв каталоге' },
    { value: stats.resolved, label: 'уже\nразобрано' },
    { value: stats.voting,   label: 'совместных\nподборок' },
  ];

  return (
    <section
      id="scene-trust"
      ref={ref}
      className="relative w-full bg-[#0d1a0c] overflow-hidden py-14 sm:py-16 px-5 sm:px-8"
    >
      <BgOrbs variant="warm" />

      <div className="relative max-w-5xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-4 sm:gap-x-6">
          {items.map((s, i) => (
            <motion.div
              key={i}
              initial={reduced ? false : { opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-center"
            >
              <div className="font-display font-bold text-white leading-none tracking-[-0.02em]"
                   style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)' }}>
                <CountUp target={s.value} active={inView} reduced={reduced} />
              </div>
              <div className="font-body text-white/65 text-[12px] sm:text-[13px] mt-2 leading-snug whitespace-pre-line">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────── CountUp ────────────── */

function CountUp({ target, active, reduced }: { target: number; active: boolean; reduced: boolean }) {
  const [n, setN] = useState(reduced ? target : 0);

  useEffect(() => {
    if (reduced || !active) {
      setN(target);
      return;
    }
    const duration = 1400;
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setN(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, reduced]);

  return <>{formatStatValue(n)}</>;
}
